import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAbrirNovo } from '../hooks/useAbrirNovo';
import { useNavigate } from 'react-router-dom';
import aviso, { textoDoErro, SEM_SERVIDOR } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuExternalLink, LuCheck, LuRotateCcw, LuListTodo,
    LuTriangleAlert, LuCalendar, LuCircleCheck, LuChevronLeft, LuChevronRight, LuTrash2, LuDownload,
} from 'react-icons/lu';
import { exportarListaCsv } from '../services/exportarCsv';
import PageHeader from '../components/PageHeader';
import Select from '../components/Select';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import { ListRow, PerPage, RowAction, RowActions, RowConfirm, ListCard, ListCards, CardConfirm } from '../components/ListKit';
import { useCelular } from '../hooks/useCelular';
import LossReasonModal from '../components/LossReasonModal';
import WinDealProductModal from '../components/WinDealProductModal';
import OportunidadeDrawer from '../components/OportunidadeDrawer';
import '../components/ListKit.css';
import { useRecorte } from '../hooks/useRecorte';
import { useGlobalNotification } from '../contexts/GlobalNotificationContext';
import './TarefasPage.css';
import { TarefaService, TipoTarefaService, type Tarefa, type TipoTarefa, type DesfechoTarefa } from '../services/TarefaService';
import { AgendaService, type AgendaEvent } from '../services/AgendaService';
import { apiFetch } from '../services/api';
import { nomeEtapa, nomeOrigem } from '../data/etapas';
import { PERMISSOES, temPermissao } from '../config/permissions';
import { ACAO_ASSISTENTE_EVENT } from '../services/AssistenteService';

/**
 * Tarefas — o que está no meu nome e o que já passou da hora.
 * docs/designsystem/propostas/notificacoes.html §5 (direção C, fase 1).
 *
 * A tela existe porque o sino era o ÚNICO lugar onde a tarefa de um vendedor
 * aparecia fora do negócio — e por isso as vencidas ficavam eternas ali. Aqui
 * elas se resolvem em lote, com o desfecho certo: a tarefa padrão só se encerra
 * pelo desfecho (que move a etapa e cria a próxima), nunca por um "concluir"
 * solto. O "Concluir" direto existe só na tarefa manual, que não alimenta o trilho.
 */

const apiUrl = import.meta.env.VITE_API_URL;
const POR_PAGINA = [25, 50, 100];

type Aba = 'hoje' | 'vencidas' | 'proximos' | 'concluidas';

interface Linha {
    chave: string;
    tipo: 'tarefa' | 'compromisso';
    titulo: string;
    contato: string | null;
    statusId: number | null;
    /** Prazo da tarefa ou início do compromisso. */
    quando: Date | null;
    /** De quando é a oportunidade, de onde veio e quanto vale — "de quem é isto?". */
    criadaEm: Date | null;
    origem: string | null;
    valor: number | null;
    /** Compromisso e tarefa manual têm hora marcada; a tarefa padrão vale o dia inteiro. */
    comHora: boolean;
    tarefa?: Tarefa;
    conversaId?: number | null;
    responsavelId?: number | null;
}

interface UsuarioResumo { userId: number; userName: string }

const inicioDoDia = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const maisDias = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const hora = (d: Date | null) =>
    d ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';
const diaCurto = (d: Date | null) =>
    d ? d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '') : '—';

/** Quando a oportunidade entrou, em relativo: é o que diz se a tarefa é de um lead
 *  fresco ou de um negócio que já está há semanas parado. */
const entrada = (d: Date) => {
    const dias = Math.max(0, Math.round((inicioDoDia(new Date()).getTime() - inicioDoDia(d).getTime()) / 86400000));
    if (dias === 0) return 'hoje';
    if (dias === 1) return 'ontem';
    if (dias < 31) return `há ${dias} dias`;
    return `em ${d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '')}`;
};

const dinheiro = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);

/** "há 5 dias" conta dia de calendário, não 24h — o prazo da tarefa padrão é o dia. */
const diasDeAtraso = (d: Date) =>
    Math.max(0, Math.round((inicioDoDia(new Date()).getTime() - inicioDoDia(d).getTime()) / 86400000));

const TarefasPage = () => {
    const navigate = useNavigate();
    const celular = useCelular();
    /* Filtrar por outro responsável: a API só aceita `responsavelId` de outra
       pessoa com `conversas.todos` (o admin tem por padrão). */
    const verTodos = temPermissao(PERMISSOES.conversasTodos);
    const meuId = useMemo(() => {
        try { return Number(JSON.parse(localStorage.getItem('user') || '{}')?.id) || null; }
        catch { return null; }
    }, []);

    /* Recorte de trabalho: a aba, o responsavel e a busca desta tela sao o
       proprio recorte - sair para a agenda ou para a oportunidade e voltar
       encontrava tudo em 'vencidas', minhas, pagina 1. */
    const [recorte, gravarRecorte] = useRecorte('tf:recorte', {
        aba: 'vencidas' as Aba, busca: '', pagina: 1, porPagina: 25,
        responsavelId: meuId as number | null,
    });
    const [aba, setAba] = useState<Aba>(recorte.aba);
    const [tarefas, setTarefas] = useState<Tarefa[]>([]);
    const [eventos, setEventos] = useState<AgendaEvent[]>([]);
    const [contagens, setContagens] = useState<Record<Aba, number>>({ hoje: 0, vencidas: 0, proximos: 0, concluidas: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [busca, setBusca] = useState(recorte.busca);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    const [responsavelId, setResponsavelId] = useState<number | null>(recorte.responsavelId);
    useEffect(() => {
        gravarRecorte({ aba, busca, pagina, porPagina, responsavelId });
    }, [aba, busca, pagina, porPagina, responsavelId, gravarRecorte]);
    const [usuarios, setUsuarios] = useState<UsuarioResumo[]>([]);
    const [ocupada, setOcupada] = useState<number | null>(null);
    /* Linha pedindo confirmação de exclusão — a pergunta nasce na própria linha. */
    const [aExcluir, setAExcluir] = useState<number | null>(null);
    const [excluindo, setExcluindo] = useState(false);
    const { recarregarResumo } = useGlobalNotification();
    /* Conversa aberta no painel lateral — ver sem sair da lista. */
    const [oportunidadeAberta, setOportunidadeAberta] = useState<number | null>(null);

    /* Desfecho que precisa de um passo antes (motivo da perda, produto, temperatura)
       fica guardado aqui até o modal responder — a mesma mecânica da tela da oportunidade. */
    const [pendente, setPendente] = useState<{ tarefa: Tarefa; desfecho: DesfechoTarefa } | null>(null);
    const [modalPerda, setModalPerda] = useState(false);
    const [modalProduto, setModalProduto] = useState(false);

    /* Nova tarefa: a que nasce aqui é sempre manual e sem oportunidade — a tarefa
       de um negócio nasce dentro dele, pelo trilho. Por isso o formulário é curto. */
    /* A paleta do Ctrl+K pediu "Nova tarefa": abre o formulário direto. */
    useAbrirNovo(() => setNovaAberta(true));

    const [novaAberta, setNovaAberta] = useState(false);
    const [novaDescricao, setNovaDescricao] = useState('');
    const [novaData, setNovaData] = useState('');
    const [novaHora, setNovaHora] = useState('');
    const [novaTipo, setNovaTipo] = useState('');
    const [novaResponsavel, setNovaResponsavel] = useState<number | null>(null);
    const [tipos, setTipos] = useState<TipoTarefa[]>([]);
    const [erroNova, setErroNova] = useState('');
    const [salvandoNova, setSalvandoNova] = useState(false);

    /* O intervalo sai daqui, do navegador, que é quem sabe o fuso de quem está
       olhando — a API não tem como saber que dia é "hoje" para esta pessoa. */
    const intervalo = useCallback((qual: Aba) => {
        const hoje = inicioDoDia(new Date());
        if (qual === 'hoje') return { de: hoje, ate: maisDias(hoje, 1), estado: 'open' as const };
        if (qual === 'vencidas') return { de: null, ate: hoje, estado: 'open' as const };
        if (qual === 'proximos') return { de: maisDias(hoje, 1), ate: maisDias(hoje, 8), estado: 'open' as const };
        /* Concluídas: a API não guarda QUANDO a tarefa foi concluída, então a régua
           dos 30 dias é o prazo dela. É a aproximação possível hoje. */
        return { de: maisDias(hoje, -30), ate: maisDias(hoje, 1), estado: 'done' as const };
    }, []);

    const buscarTarefas = useCallback(async (qual: Aba) => {
        const { de, ate, estado } = intervalo(qual);
        return TarefaService.getAll({
            responsavelId: responsavelId ?? undefined,
            minhas: !verTodos || responsavelId === meuId,
            estado,
            de: de ? de.toISOString() : undefined,
            ate: ate ? ate.toISOString() : undefined,
        }).catch(() => [] as Tarefa[]);
    }, [intervalo, responsavelId, verTodos, meuId]);

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            const [lista, agenda, ...outras] = await Promise.all([
                buscarTarefas(aba),
                /* Compromisso não tem dono na API, então só entra quando a lista é a minha —
                   numa visão de outra pessoa ele diria "isto é dela", e não diz. */
                (aba === 'hoje' || aba === 'proximos') && responsavelId === meuId
                    ? AgendaService.getAll().catch(() => [] as AgendaEvent[])
                    : Promise.resolve([] as AgendaEvent[]),
                ...(['hoje', 'vencidas', 'proximos', 'concluidas'] as Aba[])
                    .filter(a => a !== aba).map(a => buscarTarefas(a)),
            ]);
            setTarefas(lista);
            setEventos(agenda);

            const restantes = (['hoje', 'vencidas', 'proximos', 'concluidas'] as Aba[]).filter(a => a !== aba);
            const novas = { hoje: 0, vencidas: 0, proximos: 0, concluidas: 0 } as Record<Aba, number>;
            novas[aba] = lista.length;
            restantes.forEach((a, i) => { novas[a] = (outras[i] as Tarefa[]).length; });
            setContagens(novas);
            /* O número da sidebar é outra chamada, que só se repete a cada minuto: sem
               pedir aqui, resolver uma vencida deixava o menu contando a que já saiu. */
            recarregarResumo();
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setIsLoading(false);
        }
    }, [aba, buscarTarefas, responsavelId, meuId, recarregarResumo]);

    useEffect(() => { carregar(); }, [carregar]);

    /* Uma ação do agente (painel ao lado da tela) pode criar, concluir ou adiar
       tarefa: a lista recarrega sozinha. */
    useEffect(() => {
        const aoAgir = () => { carregar(); };
        window.addEventListener(ACAO_ASSISTENTE_EVENT, aoAgir);
        return () => window.removeEventListener(ACAO_ASSISTENTE_EVENT, aoAgir);
    }, [carregar]);
    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [aba, busca, porPagina, responsavelId]);

    useEffect(() => {
        TipoTarefaService.getAll().then(setTipos).catch(() => setTipos([]));
    }, []);

    useEffect(() => {
        if (!verTodos) return;
        apiFetch(`${apiUrl}/api/Usuario/BuscarTodos`)
            .then(r => r.json())
            .then(r => setUsuarios(Array.isArray(r?.dados) ? r.dados : []))
            .catch(() => setUsuarios([]));
    }, [verTodos]);

    /* ─── As linhas: tarefa e compromisso na mesma lista ─── */

    const linhas = useMemo<Linha[]>(() => {
        const { de, ate } = intervalo(aba);
        const daTarefa = (t: Tarefa): Linha => ({
            chave: `t-${t.tarefaId}`,
            tipo: 'tarefa',
            titulo: t.descricao,
            contato: t.nomeContato || null,
            statusId: t.statusId ?? null,
            quando: t.dataRetorno ? new Date(t.dataRetorno) : null,
            criadaEm: t.criadaEm ? new Date(t.criadaEm) : null,
            origem: nomeOrigem(t.origemId),
            valor: t.valor ?? null,
            comHora: t.origem === 'manual',
            tarefa: t,
            conversaId: t.conversaId,
            responsavelId: t.responsavelId,
        });

        const doEvento = (e: AgendaEvent): Linha => ({
            chave: `a-${e.agendaId}`,
            tipo: 'compromisso',
            titulo: e.descricao,
            contato: null,
            statusId: null,
            quando: new Date(e.dataInicio),
            criadaEm: null,
            origem: null,
            valor: null,
            comHora: true,
        });

        const noIntervalo = (d: Date) => (!de || d >= de) && (!ate || d < ate);
        const todas = [
            ...tarefas.map(daTarefa),
            ...eventos.filter(e => noIntervalo(new Date(e.dataInicio))).map(doEvento),
        ];

        const termo = busca.trim().toLowerCase();
        const filtradas = termo
            ? todas.filter(l => `${l.titulo} ${l.contato ?? ''}`.toLowerCase().includes(termo))
            : todas;

        return filtradas.sort((a, b) => {
            const ta = a.quando?.getTime() ?? 0, tb = b.quando?.getTime() ?? 0;
            /* Vencidas: da mais velha para a mais nova. Nas outras, o que tem hora
               marcada vem primeiro, porque é o que tem lugar no dia. */
            if (aba === 'vencidas' || aba === 'concluidas') return ta - tb;
            if (a.comHora !== b.comHora) return a.comHora ? -1 : 1;
            return ta - tb;
        });
    }, [tarefas, eventos, busca, aba, intervalo]);

    const totalPaginas = Math.max(1, Math.ceil(linhas.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = linhas.slice(inicio, inicio + porPagina);

    /* ─── Resolver a tarefa ─── */

    const registrar = async (tarefa: Tarefa, desfecho: DesfechoTarefa,
        extra: { motivoPerdaId?: number; motivoPerdaDetalhe?: string; tipoLeadId?: number } = {}) => {
        setOcupada(tarefa.tarefaId);
        const carregando = aviso.carregando('Registrando…');
        try {
            const r = await TarefaService.registrarDesfecho(tarefa.tarefaId, { desfechoId: desfecho.desfechoId, ...extra });
            if (!r.sucesso) {
                // Ganho sem produto: a API recusa com a mesma mensagem do quadro; abre a vinculação.
                if (desfecho.acao === 'move' && desfecho.statusDestinoId === 3 && r.mensagem?.toLowerCase().includes('produto')) {
                    aviso.fechar(carregando);
                    setPendente({ tarefa, desfecho });
                    setModalProduto(true);
                    return;
                }
                throw new Error(r.mensagem || 'Não foi possível registrar o desfecho.');
            }
            aviso.ok(r.mensagem || 'Desfecho registrado.', { id: carregando });
            setPendente(null);
            await carregar();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível registrar o desfecho.'), { id: carregando });
        } finally {
            setOcupada(null);
        }
    };

    /** Mesma escada da tela da oportunidade: o que precisa de um passo antes abre o modal. */
    const escolherDesfecho = (tarefa: Tarefa, desfechoId: string) => {
        const desfecho = tarefa.desfechos.find(d => String(d.desfechoId) === desfechoId);
        if (!desfecho) return;
        if (desfecho.acao === 'lose') { setPendente({ tarefa, desfecho }); setModalPerda(true); return; }
        registrar(tarefa, desfecho);
    };

    /** O desfecho de reagendar, que é o "adiar" de um clique. O número de dias é o do modelo. */
    const desfechoDeAdiar = (t: Tarefa) => t.desfechos.find(d => d.acao === 'reschedule') ?? null;

    const concluirManual = async (t: Tarefa) => {
        setOcupada(t.tarefaId);
        try {
            const r = await TarefaService.atualizar({
                tarefaId: t.tarefaId,
                descricao: t.descricao,
                estaConcluida: true,
                tipoTarefaId: t.tipoTarefaId,
                dataRetorno: t.dataRetorno,
                responsavelId: t.responsavelId,
            });
            if (r.sucesso) { aviso.ok('Tarefa concluída.'); await carregar(); }
            else aviso.erro(r.mensagem || 'Não foi possível concluir a tarefa.');
        } catch {
            aviso.erro('Não foi possível concluir a tarefa.');
        } finally {
            setOcupada(null);
        }
    };

    /* Soft delete na API, como o resto da base. A tarefa padrão excluída não é
       recriada: o negócio fica sem próximo passo até alguém mover a etapa. */
    const excluir = async (t: Tarefa) => {
        setExcluindo(true);
        try {
            const r = await TarefaService.remover(t.tarefaId);
            if (r.sucesso) { aviso.ok('Tarefa excluída.'); setAExcluir(null); await carregar(); }
            else aviso.erro(r.mensagem || 'Não foi possível excluir a tarefa.');
        } catch {
            aviso.erro('Não foi possível excluir a tarefa.');
        } finally {
            setExcluindo(false);
        }
    };

    const abrirNova = () => {
        const hoje = new Date();
        setNovaDescricao(''); setNovaHora(''); setErroNova('');
        setNovaData(`${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-${String(hoje.getDate()).padStart(2, '0')}`);
        setNovaTipo(tipos[0] ? String(tipos[0].tipoTarefaId) : '');
        setNovaResponsavel(responsavelId ?? meuId);
        setNovaAberta(true);
    };

    const salvarNova = async () => {
        const texto = novaDescricao.trim();
        if (!texto) { setErroNova('Escreva o que precisa ser feito.'); return; }
        if (!novaTipo) { setErroNova('Escolha o tipo da tarefa.'); return; }
        if (!novaData) { setErroNova('Informe o prazo.'); return; }

        const [ano, mes, dia] = novaData.split('-').map(Number);
        const [h, m] = (novaHora || '09:00').split(':').map(Number);
        const prazo = new Date(ano, (mes || 1) - 1, dia || 1, h || 0, m || 0, 0, 0);

        setSalvandoNova(true);
        try {
            const r = await TarefaService.criar({
                descricao: texto,
                conversaId: null,
                tipoTarefaId: Number(novaTipo),
                dataRetorno: prazo.toISOString(),
                responsavelId: novaResponsavel,
            });
            if (r.sucesso) {
                aviso.ok('Tarefa criada.');
                setNovaAberta(false);
                await carregar();
            } else setErroNova(r.mensagem || 'Erro ao criar a tarefa.');
        } catch {
            setErroNova(SEM_SERVIDOR);
        } finally {
            setSalvandoNova(false);
        }
    };

    const abrir = (l: Linha) => {
        if (l.tipo === 'compromisso') { navigate('/agenda'); return; }
        /* Painel lateral, não outra tela: quem está zerando vencidas volta para a
           lista no ponto em que estava. A tela cheia continua a um clique, no painel. */
        if (l.conversaId) { setOportunidadeAberta(l.conversaId); return; }
        aviso.info('Esta tarefa não está ligada a nenhuma oportunidade.');
    };

    /* ─── Cabeçalho ─── */

    const ABAS = [
        { id: 'hoje', label: 'Hoje', icon: <LuListTodo />, count: contagens.hoje },
        { id: 'vencidas', label: 'Vencidas', icon: <LuTriangleAlert />, count: contagens.vencidas },
        { id: 'proximos', label: 'Próximos 7 dias', icon: <LuCalendar />, count: contagens.proximos },
        { id: 'concluidas', label: 'Concluídas', icon: <LuCircleCheck />, count: contagens.concluidas },
    ];

    /* Quem é o dono só aparece quando a lista é de mais de uma pessoa — numa lista
       de uma pessoa só a coluna repetiria o filtro em todas as linhas. */
    const mostrarResponsavel = verTodos && responsavelId === null;
    /* "Próximo passo" e "Ações" são largura fixa: o que mora nelas é controle, não
       texto, e controle espremido some (o "Adiar 2 dias" vira "Adia"). O resto do
       espaço vai para as colunas de texto, que sabem truncar. */
    const COLUNAS = mostrarResponsavel
        ? 'minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.2fr) minmax(0, 1fr) 96px 300px 78px'
        : 'minmax(0, 2.2fr) minmax(0, 1.6fr) minmax(0, 1.1fr) 96px 300px 78px';

    const nomeUsuario = (id?: number | null) =>
        usuarios.find(u => u.userId === id)?.userName?.trim() || '—';

    const tituloPrazo = aba === 'concluidas' ? 'Prazo' : aba === 'vencidas' ? 'Venceu' : 'Quando';

    /* Exporta a aba aberta com a busca aplicada, todas as páginas. Só o
       administrador, como em Contatos: a lista carrega nome de cliente e valor. */
    const exportar = () => exportarListaCsv(`tarefas_${aba}`, linhas, [
        { rotulo: 'Tarefa', valor: l => l.titulo },
        { rotulo: 'Tipo', valor: l => (l.tipo === 'compromisso' ? 'Compromisso' : 'Tarefa') },
        { rotulo: 'Contato', valor: l => l.contato },
        { rotulo: 'Responsável', valor: l => (l.responsavelId ? nomeUsuario(l.responsavelId) : '') },
        { rotulo: 'Etapa', valor: l => (l.statusId ? nomeEtapa(l.statusId) : '') },
        { rotulo: 'Valor', valor: l => l.valor, tipo: 'numero' },
        { rotulo: tituloPrazo, valor: l => l.quando?.toISOString(), tipo: 'data' },
        { rotulo: 'Hora', valor: l => (l.comHora && l.quando
            ? l.quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '') },
        { rotulo: 'Situação', valor: l => (l.tipo === 'compromisso' ? 'Compromisso'
            : l.tarefa && l.tarefa.estado !== 'open' ? (l.tarefa.desfechoRotulo || 'Concluída') : 'Aberta') },
        { rotulo: 'Origem', valor: l => l.origem },
    ]);

    /* A pílula de prazo é a mesma na tabela e no cartão: vencida fica em
       `is-late`, hoje em `is-hoje`. Sai do meio do JSX para não existir em duas
       versões que podem divergir. */
    const pilulaPrazo = (l: Linha) => {
        const atrasada = aba === 'vencidas' && l.quando;
        return (
            <span className={`tf-prazo ${atrasada ? 'is-late' : ''} ${aba === 'hoje' ? 'is-hoje' : ''}`}>
                {atrasada
                    ? `há ${diasDeAtraso(l.quando!)} ${diasDeAtraso(l.quando!) === 1 ? 'dia' : 'dias'}`
                    : aba === 'hoje'
                        ? (l.comHora ? `hoje, ${hora(l.quando)}` : 'hoje')
                        : `${diaCurto(l.quando)}${l.comHora ? `, ${hora(l.quando)}` : ''}`}
            </span>
        );
    };

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {aba === 'vencidas' ? 'Nada vencido' : aba === 'hoje' ? 'Nada para hoje'
                    : aba === 'proximos' ? 'Nada nos próximos 7 dias' : 'Nenhuma tarefa concluída'}
            </div>
            <p className="lk-blank-text">
                {contagens.vencidas > 0 && aba !== 'vencidas'
                    ? `Você tem ${contagens.vencidas} ${contagens.vencidas === 1 ? 'tarefa vencida' : 'tarefas vencidas'}.`
                    : 'Quando uma oportunidade entra numa etapa com tarefa padrão, o próximo passo aparece aqui.'}
            </p>
            {contagens.vencidas > 0 && aba !== 'vencidas' && (
                <button type="button" className="lk-btn is-primary" onClick={() => setAba('vencidas')}>
                    <LuTriangleAlert size={14} /> Ver as vencidas
                </button>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, linhas.length)} de {linhas.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} />
                </button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Tarefas"
                subtitle="O que está no seu nome — e o que já passou da hora"
                tabs={ABAS}
                activeTab={aba}
                onTabChange={id => setAba(id as Aba)}
                actions={
                    <div className="lk-actions">
                        {verTodos && (
                            <button type="button" className="lk-btn" onClick={exportar} disabled={isLoading}>
                                <LuDownload size={14} /> Exportar
                            </button>
                        )}
                        <button type="button" className="lk-btn is-primary" onClick={abrirNova}>
                            <LuPlus size={14} /> Nova tarefa
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar tarefa ou contato…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    {verTodos && (
                        <div className="tf-responsavel">
                            <Select
                                size="control"
                                value={responsavelId === null ? 'todos' : String(responsavelId)}
                                onChange={v => setResponsavelId(v === 'todos' ? null : Number(v))}
                                options={[
                                    { value: 'todos', label: 'Responsável: todos' },
                                    ...usuarios.map(u => ({
                                        value: String(u.userId),
                                        label: `Responsável: ${u.userId === meuId ? 'eu' : u.userName.trim()}`,
                                        meta: u.userId === meuId ? u.userName.trim() : undefined,
                                    })),
                                ]}
                            />
                        </div>
                    )}
                    <span className="lk-count">
                        {linhas.length} {linhas.length === 1 ? 'item' : 'itens'}
                    </span>
                </div>

                {/* Celular: a grade tem duas colunas de largura fixa (96px do prazo
                    e 300px do próximo passo) e mede 860px numa área útil de 358 —
                    só a tarefa e o contato apareciam. A linha vira cartão: o que
                    identifica (tarefa, negócio) e em que pé está (prazo,
                    responsável) ficam aqui; o desfecho se resolve na oportunidade,
                    um toque adiante. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(l => {
                                    const t = l.tarefa;
                                    if (t && aExcluir === t.tarefaId) return (
                                        <CardConfirm key={l.chave}
                                            question={t.origem === 'default' && t.estado === 'open'
                                                ? `Excluir "${l.titulo}"? O negócio fica sem próximo passo.`
                                                : `Excluir "${l.titulo}"?`}
                                            busy={excluindo}
                                            onCancel={() => setAExcluir(null)}
                                            onConfirm={() => excluir(t)} />
                                    );
                                    return (
                                        <ListCard
                                            key={l.chave}
                                            titulo={
                                                <span className="tf-titulo">
                                                    <span className="tf-texto">{l.titulo}</span>
                                                    {l.tipo === 'compromisso' && (
                                                        <span className="tf-marca"><LuCalendar size={11} /> Agenda</span>
                                                    )}
                                                </span>
                                            }
                                            apoio={l.contato
                                                || (l.tipo === 'compromisso' ? 'compromisso da agenda' : 'sem negócio vinculado')}
                                            estado={
                                                <>
                                                    {pilulaPrazo(l)}
                                                    {mostrarResponsavel && <span>· {nomeUsuario(l.responsavelId)}</span>}
                                                </>
                                            }
                                            label={l.tipo === 'compromisso' ? `Abrir ${l.titulo} na agenda` : `Abrir ${l.titulo}`}
                                            onOpen={() => abrir(l)}
                                            acoes={t && (
                                                <RowAction icon={<LuTrash2 size={15} />} label="Excluir tarefa" danger
                                                    disabled={ocupada === t.tarefaId}
                                                    onClick={() => setAExcluir(t.tarefaId)} />
                                            )}
                                        />
                                    );
                                })}
                        </ListCards>
                        {!isLoading && linhas.length === 0 && listaVazia}
                        {!isLoading && linhas.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Tarefa</div>
                            <div className="lk-head">Contato</div>
                            {mostrarResponsavel && <div className="lk-head">Responsável</div>}
                            <div className="lk-head">Etapa</div>
                            <div className="lk-head">{tituloPrazo}</div>
                            <div className="lk-head">Próximo passo</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(l => {
                                    const t = l.tarefa;
                                    const adiar = t && t.estado === 'open' ? desfechoDeAdiar(t) : null;
                                    if (t && aExcluir === t.tarefaId) return (
                                        <RowConfirm key={l.chave}
                                            question={t.origem === 'default' && t.estado === 'open'
                                                ? `Excluir "${l.titulo}"? O negócio fica sem próximo passo.`
                                                : `Excluir "${l.titulo}"?`}
                                            busy={excluindo}
                                            onCancel={() => setAExcluir(null)}
                                            onConfirm={() => excluir(t)} />
                                    );
                                    return (
                                        <ListRow key={l.chave} columns={COLUNAS}>
                                            <span className="lk-cell lk-strong">
                                                <span className="tf-titulo">
                                                    <span className="tf-texto">{l.titulo}</span>
                                                    {l.tipo === 'compromisso' && (
                                                        <span className="tf-marca"><LuCalendar size={11} /> Agenda</span>
                                                    )}
                                                </span>
                                            </span>
                                            <span className="lk-cell">
                                                {l.contato ? (
                                                    <span className="tf-contato">
                                                        <span className="tf-contato-nome">{l.contato}</span>
                                                        {(l.origem || l.criadaEm) && (
                                                            <span className="tf-contato-sub">
                                                                {[l.origem, l.criadaEm ? `entrou ${entrada(l.criadaEm)}` : null]
                                                                    .filter(Boolean).join(' · ')}
                                                            </span>
                                                        )}
                                                    </span>
                                                ) : (
                                                    <span className="lk-muted">
                                                        {l.tipo === 'compromisso' ? '—' : 'sem negócio vinculado'}
                                                    </span>
                                                )}
                                            </span>
                                            {mostrarResponsavel && (
                                                <span className="lk-cell lk-muted"><span>{nomeUsuario(l.responsavelId)}</span></span>
                                            )}
                                            <span className="lk-cell">
                                                <span className="tf-contato">
                                                    <span className="lk-muted">{nomeEtapa(l.statusId)}</span>
                                                    {!!l.valor && <span className="tf-contato-sub">{dinheiro(l.valor)}</span>}
                                                </span>
                                            </span>
                                            <span className="lk-cell">{pilulaPrazo(l)}</span>
                                            <span className="lk-cell">
                                                {l.tipo === 'compromisso' ? (
                                                    <span className="tf-sem-passo">compromisso — não se conclui</span>
                                                ) : t && t.estado !== 'open' ? (
                                                    <span className="tf-sem-passo">{t.desfechoRotulo || 'concluída'}</span>
                                                ) : t && t.origem === 'default' && t.desfechos.length > 0 ? (
                                                    <span className="tf-passo">
                                                        <Select
                                                            size="control"
                                                            portal
                                                            value=""
                                                            placeholder="Escolher desfecho"
                                                            disabled={ocupada === t.tarefaId}
                                                            onChange={v => escolherDesfecho(t, v)}
                                                            options={t.desfechos.map(d => ({ value: String(d.desfechoId), label: d.rotulo }))}
                                                        />
                                                        {adiar && (
                                                            <button type="button" className="tf-adiar"
                                                                disabled={ocupada === t.tarefaId}
                                                                onClick={() => registrar(t, adiar)}>
                                                                <LuRotateCcw size={12} />
                                                                Adiar {adiar.diasReagendamento ?? 0} {adiar.diasReagendamento === 1 ? 'dia' : 'dias'}
                                                            </button>
                                                        )}
                                                    </span>
                                                ) : t ? (
                                                    <button type="button" className="tf-adiar"
                                                        disabled={ocupada === t.tarefaId}
                                                        onClick={() => concluirManual(t)}>
                                                        <LuCheck size={12} /> Concluir
                                                    </button>
                                                ) : null}
                                            </span>
                                            <RowActions>
                                                <RowAction icon={<LuExternalLink size={14} />}
                                                    label={l.tipo === 'compromisso' ? 'Abrir na agenda' : 'Abrir a oportunidade'}
                                                    onClick={() => abrir(l)} />
                                                {/* Compromisso se exclui na agenda, que é o dono dele. */}
                                                {t && (
                                                    <RowAction icon={<LuTrash2 size={14} />} label="Excluir tarefa" danger
                                                        disabled={ocupada === t.tarefaId}
                                                        onClick={() => setAExcluir(t.tarefaId)} />
                                                )}
                                            </RowActions>
                                        </ListRow>
                                    );
                                })}
                        </div>
                    </div>

                    {!isLoading && linhas.length === 0 && listaVazia}

                    {!isLoading && linhas.length > 0 && rodape}
                </div>
                )}
            </div>

            <OportunidadeDrawer
                conversaId={oportunidadeAberta}
                onFechar={() => {
                    setOportunidadeAberta(null);
                    /* Quem abriu o negócio pode ter movido a etapa ou concluído a
                       tarefa lá dentro; a lista volta conferindo. */
                    carregar();
                }}
            />

            <RecordPanel
                open={novaAberta}
                onClose={() => setNovaAberta(false)}
                title={novaDescricao.trim() || 'Nova tarefa'}
                editing
                footer={
                    <>
                        <button type="button" className="rp-btn" disabled={salvandoNova}
                            onClick={() => setNovaAberta(false)}>Cancelar</button>
                        <button type="button" className="rp-btn is-primary" disabled={salvandoNova}
                            onClick={salvarNova}>{salvandoNova ? 'Salvando…' : 'Salvar'}</button>
                    </>
                }
            >
                <PanelSection title="Tarefa">
                    <div className="rp-field">
                        <label className="rp-label" htmlFor="tf-desc">O que precisa ser feito</label>
                        <input id="tf-desc" className="rp-input" type="text" autoFocus
                            value={novaDescricao} placeholder="Ex.: Ligar para confirmar o endereço"
                            onChange={e => { setNovaDescricao(e.target.value); setErroNova(''); }} />
                        {erroNova && <span className="rp-error">{erroNova}</span>}
                    </div>
                    <div className="rp-field">
                        <label className="rp-label">Tipo</label>
                        <Select value={novaTipo} onChange={v => { setNovaTipo(v); setErroNova(''); }}
                            placeholder="Escolher tipo"
                            options={tipos.map(t => ({ value: String(t.tipoTarefaId), label: t.nome }))} />
                    </div>
                    <div className="tf-duo">
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="tf-data">Prazo</label>
                            <input id="tf-data" className="rp-input" type="date"
                                value={novaData} onChange={e => { setNovaData(e.target.value); setErroNova(''); }} />
                        </div>
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="tf-hora">Hora (opcional)</label>
                            <input id="tf-hora" className="rp-input" type="time"
                                value={novaHora} onChange={e => setNovaHora(e.target.value)} />
                        </div>
                    </div>
                    {verTodos && usuarios.length > 0 && (
                        <div className="rp-field">
                            <label className="rp-label">Responsável</label>
                            <Select value={novaResponsavel ? String(novaResponsavel) : ''}
                                onChange={v => setNovaResponsavel(Number(v))}
                                placeholder="Escolher responsável"
                                options={usuarios.map(u => ({
                                    value: String(u.userId),
                                    label: u.userId === meuId ? `${u.userName.trim()} (você)` : u.userName.trim(),
                                }))} />
                        </div>
                    )}
                    <span className="tf-dica">
                        A tarefa criada aqui é manual e não fica ligada a nenhuma oportunidade — por isso
                        se conclui com um clique, sem desfecho. Para o próximo passo de um negócio, use o
                        trilho dentro da oportunidade.
                    </span>
                </PanelSection>
            </RecordPanel>

            <LossReasonModal
                isOpen={modalPerda}
                dealTitle={pendente?.tarefa.nomeContato || pendente?.tarefa.descricao || ''}
                onCancel={() => { setModalPerda(false); setPendente(null); }}
                onConfirm={async (motivoPerdaId, motivoPerdaDetalhe) => {
                    setModalPerda(false);
                    if (pendente) await registrar(pendente.tarefa, pendente.desfecho, { motivoPerdaId, motivoPerdaDetalhe });
                }}
            />
            <WinDealProductModal
                isOpen={modalProduto}
                dealId={pendente?.tarefa.conversaId ?? 0}
                dealTitle={pendente?.tarefa.nomeContato || ''}
                onCancel={() => { setModalProduto(false); setPendente(null); }}
                onConfirm={async () => {
                    setModalProduto(false);
                    if (pendente) await registrar(pendente.tarefa, pendente.desfecho);
                }}
            />
        </div>
    );
};

export default TarefasPage;
